/**
 * Sélecteur d'entreprise ou de contact pour un champ de référence — chunk 9.AC.1,
 * fermeture de la dette `D-NEW-FORMS-1DII-REF-NO-PICKER`.
 *
 * ## Ce que ça débloque
 *
 * `company_ref` et `contact_ref` rendaient un `<input disabled>` dans TOUS les
 * modes, y compris `fill`. Un formulaire dont un champ de référence est requis
 * était donc insoumettable : la validation `mode: "submit"` l'exige, rien ne
 * permettait de le saisir. Le dossier ne pouvait jamais devenir prêt.
 *
 * ## Ce que ça ne débloque PAS, et pourquoi
 *
 * Ce composant n'est monté que par le parcours conseiller **authentifié**. Le
 * parcours public ne passe pas `renderReferenceField` au `FormRenderer`, et ne
 * peut pas le passer par accident — il faudrait l'écrire. Lister les entreprises
 * et contacts du cabinet à un visiteur anonyme reviendrait à offrir le
 * portefeuille client à qui possède un lien.
 *
 * Côté serveur, la garde correspondante refuse de créer un lien public vers une
 * version dont un champ de référence est requis
 * (`INTAKE_LINKS_VERSION_NOT_PUBLIC_FILLABLE`) : l'impasse est fermée des deux
 * côtés, pas seulement caché dans l'interface.
 */

import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useCompanies } from "../../lib/queries/companies";
import { useContacts } from "../../lib/queries/contacts";

type Props = {
  readonly type: "company_ref" | "contact_ref";
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
  readonly inputId: string;
  readonly describedBy: string | undefined;
  /**
   * Entreprise du dossier, quand elle est connue. Restreint la liste des
   * contacts à ceux de cette entreprise : proposer les contacts de tout le
   * cabinet inviterait à rattacher le contact d'un client à un autre.
   */
  readonly companyId?: string | null;
};

const selectCls =
  "mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 " +
  "shadow-sm focus-visible:border-brand-500 focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-brand-500 disabled:bg-slate-50 disabled:text-slate-500";

export function ReferenceFieldPicker({
  type,
  value,
  onChange,
  inputId,
  describedBy,
  companyId,
}: Props) {
  const { t } = useTranslation();

  // `useContacts` s'auto-désactive sans `companyId` (`enabled: !!companyId`), ce
  // qui coupe la requête inutile sur un champ `company_ref` sans code en plus.
  const entreprises = useCompanies({ limit: 100 });
  const contacts = useContacts(type === "contact_ref" ? (companyId ?? undefined) : undefined, {
    limit: 100,
  });

  const source = type === "company_ref" ? entreprises : contacts;

  const options = useMemo(() => {
    const items = (source.data?.items ?? []) as Record<string, unknown>[];
    return items
      .map((it) => ({
        id: String(it.id ?? ""),
        label:
          type === "company_ref"
            ? String(it.legal_name ?? it.trade_name ?? "")
            : [it.first_name, it.last_name].filter(Boolean).join(" ") ||
              String(it.email ?? ""),
      }))
      .filter((o) => o.id.length > 0 && o.label.length > 0);
  }, [source.data, type]);

  // Le contact demande une entreprise. Le dire explicitement vaut mieux qu'une
  // liste vide, qui se lit comme « il n'y a personne » alors que la vraie cause
  // est « je ne sais pas chez qui chercher ».
  if (type === "contact_ref" && !companyId) {
    return (
      <p
        id={inputId}
        aria-describedby={describedBy}
        className="mt-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600"
      >
        {t("form_templates.renderer.ref_needs_company")}
      </p>
    );
  }

  if (source.isPending) {
    return (
      <p className="mt-1 px-3 py-2 text-sm text-slate-500" aria-live="polite">
        {t("form_templates.renderer.ref_loading")}
      </p>
    );
  }

  if (source.isError) {
    return (
      <p className="mt-1 px-3 py-2 text-sm text-red-700" role="alert">
        {t("form_templates.renderer.ref_error")}
      </p>
    );
  }

  if (options.length === 0) {
    return (
      <p className="mt-1 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-600">
        {t(
          type === "company_ref"
            ? "form_templates.renderer.ref_no_company"
            : "form_templates.renderer.ref_no_contact",
        )}
      </p>
    );
  }

  return (
    <select
      id={inputId}
      aria-describedby={describedBy}
      className={selectCls}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
    >
      <option value="">{t("form_templates.renderer.ref_choose")}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
